import { useState } from "react";
import { Box, TextField, Button, CircularProgress } from "@material-ui/core";
import { useForm } from "react-hook-form";
import { useQuery, useMutation } from "react-query";
import { AxiosError } from "axios";
import { useDebounce } from "ahooks";
import { useRouter } from "next/router";
import NumberFormat from "react-number-format";
import numeral from "numeral";

import { useSnackbar } from "notistack";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { InsuredList } from "@/utils/interfaces";
import ApplicantNameAutocomplete from "./ApplicantNameAutocomplete";
import api from "@/services/api";
import { conversion, conversions } from "@/services/ga";
import Links from "@/constants/Links";

const applicantSetupSchema = yup.object().shape({
  nickname: yup.string().required().label("Applicant Name"),
  primaryWebsite: yup.string().label("Primary Website"),
  revenue: yup.string().required().label("Annual Revenue"),
  additionalComments: yup.string().label("Additional comments"),
});

const postStartApplication = ({
  nickname,
  primaryWebsite,
  revenue,
  additionalComments,
}) =>
  api.post(`/v1/getcyber-application/start`, {
    nickname,
    primary_website: primaryWebsite || undefined,
    revenue: {
      amount: numeral(revenue).value() * 100,
      currency: "USD",
    },
    additional_comments: additionalComments || undefined,
  });

export default function ApplicationStartForm() {
  const router = useRouter();

  const form = useForm({
    resolver: yupResolver(applicantSetupSchema),
  });

  const { enqueueSnackbar } = useSnackbar();

  const [insuredSearchOpen, setInsuredSearchOpen] = useState(false);

  const insuredSearchTerm = form.watch("nickname");
  const setInsuredSearchTerm = (newValue) =>
    form.setValue("nickname", newValue);

  const throttledInsuredSearchTerm = useDebounce(insuredSearchTerm, {
    wait: 500,
  });

  const insuredSearch = useQuery<InsuredList, AxiosError>([
    "company-account/",
    {
      ordering: "nickname",
      search: throttledInsuredSearchTerm,
      enabled: !!throttledInsuredSearchTerm,
    },
  ]);

  const {
    mutateAsync: mutateStartApplication,
    status: mutateStartApplicationStatus,
  } = useMutation<
    { data: { url: string; share_id: string } },
    AxiosError,
    {
      nickname: string;
      primaryWebsite?: string;
      revenue: string;
      additionalComments?: string;
    }
  >(postStartApplication, {
    onSuccess: (response) => {
      if (process.env.NEXT_PUBLIC_USE_NEW_APPLICATION === "TRUE") {
        router.push(`${Links.APPLICATION}/${response.data.share_id}`);
      } else {
        window.location.replace(response.data.url);
      }
    },
    onError: (err) => {
      const errMsg = err?.response?.data
        ? Object.values(err.response.data).join(", ")
        : null;
      enqueueSnackbar(`There was an error${errMsg ? `: ${errMsg}` : "."}`, {
        variant: "error",
      });
    },
  });

  const onSubmit = ({
    nickname,
    primaryWebsite,
    revenue,
    additionalComments,
  }) => {
    conversion(conversions.submit_company_info);
    mutateStartApplication({
      nickname,
      primaryWebsite,
      revenue,
      additionalComments,
    });
  };

  return (
    <form
      noValidate
      onSubmit={form.handleSubmit(onSubmit)}
      style={{ width: "100%" }}
    >
      <Box width="100%">
        <Box mb={4}>
          <ApplicantNameAutocomplete
            name="nickname"
            control={form.control}
            options={
              (!!throttledInsuredSearchTerm && insuredSearch.data?.results) ||
              []
            }
            loading={insuredSearch.isFetching}
            setInputValue={setInsuredSearchTerm}
            open={insuredSearchOpen}
            error={!!form.errors?.nickname}
            helperText={form.errors?.nickname?.message}
            onOpen={() => {
              setInsuredSearchOpen(true);
            }}
            onClose={() => {
              setInsuredSearchOpen(false);
            }}
          />
        </Box>
        <Box mb={4}>
          <TextField
            name="primaryWebsite"
            inputRef={form.register}
            label="Primary Website"
            placeholder="example.com"
            error={!!form.errors?.primaryWebsite}
            variant="outlined"
            fullWidth
            helperText={form.errors?.primaryWebsite?.message}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box mb={4}>
          <NumberFormat
            customInput={TextField}
            thousandSeparator
            prefix="$"
            name="revenue"
            inputRef={form.register}
            label="Annual Revenue"
            placeholder="Most recent 12 months or next year's forecast"
            error={!!form.errors?.revenue}
            variant="outlined"
            fullWidth
            helperText={form.errors?.revenue?.message}
            InputLabelProps={{
              shrink: true,
            }}
            required
          />
        </Box>
        <Box mb={4}>
          <TextField
            name="additionalComments"
            inputRef={form.register}
            label="Notes"
            placeholder="Any special requests, requirements, or other details we should be aware of."
            error={!!form.errors?.additionalComments}
            variant="outlined"
            fullWidth
            multiline
            minRows={2}
            helperText={form.errors?.additionalComments?.message}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Box>
        <Box>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            size="large"
            endIcon={
              mutateStartApplicationStatus === "loading" && (
                <CircularProgress size="small" />
              )
            }
            disabled={mutateStartApplicationStatus === "loading"}
          >
            Next
          </Button>
        </Box>
      </Box>
    </form>
  );
}
